import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {api} from '../../api';
import Layout from '../components/Layout';

export default function RecordingDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [recording, setRecording] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [transcript, setTranscript] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    loadRecording();
    api(`/recordings/${id}/audio-url`).then(r => r.json()).then(data => setAudioUrl(data.url));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [id]);

  const loadRecording = () => {
    api(`/recordings/${id}`).then(r => r.json()).then(data => {
      setRecording(data);
      setTitle(data.title || '');
      setTranscript(data.transcript || '');
      if (data.status === 'TRANSCRIBING') startPolling();
    });
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const res = await api(`/recordings/${id}/transcription-status`);
      const data = await res.json();
      setRecording(data);
      setTranscript(data.transcript || '');
      if (data.status !== 'TRANSCRIBING') {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 5000);
  };

  const handleSave = async () => {
    const res = await api(`/recordings/${id}`, {
      method: 'PUT',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({ title, transcript })
    });
    if (res.ok) {
      const updated = await res.json();
      setRecording(updated);
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    await api(`/recordings/${id}`, { method: 'DELETE' });
    navigate('/recordings');
  };

  const handleReprocess = async () => {
    const res = await api(`/recordings/${id}/reprocess`, { method: 'POST' });
    if (res.ok) {
      const data = await res.json();
      setRecording(data);
      setTranscript('');
      startPolling();
    }
  };

  if (!recording) return <Layout><p className="text-gray-500">Loading...</p></Layout>;

  return (
    <Layout>
      <div className="mb-4">
        <button onClick={() => navigate('/recordings')} className="text-gray-600 hover:text-gray-900">&larr; Back to Recordings</button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{recording.title || recording.fileName}</h3>
          <div className="flex gap-2">
            {!editing ? (
              <button onClick={() => setEditing(true)} className="text-sm bg-sky-600 text-white px-3 py-1 rounded hover:bg-sky-700">Edit</button>
            ) : (
              <>
                <button onClick={() => { setEditing(false); setTitle(recording.title || ''); setTranscript(recording.transcript || ''); }} className="text-sm bg-gray-200 text-gray-700 px-3 py-1 rounded">Cancel</button>
                <button onClick={handleSave} className="text-sm bg-sky-600 text-white px-3 py-1 rounded hover:bg-sky-700">Save</button>
              </>
            )}
            {recording.status === 'FAILED' && (
              <button onClick={handleReprocess} className="text-sm bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700">Reprocess</button>
            )}
            <button onClick={() => setShowDeleteModal(true)} className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700">Delete</button>
          </div>
        </div>

        {/* Title edit */}
        {editing && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-1">Title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
        )}

        {/* Audio player */}
        {audioUrl && (
          <div className="mb-6">
            <audio controls src={audioUrl} className="w-full" />
          </div>
        )}

        {/* Status */}
        <div className="mb-4">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            recording.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
            recording.status === 'TRANSCRIBING' ? 'bg-yellow-100 text-yellow-800' :
            recording.status === 'FAILED' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
          }`}>{recording.status}</span>
        </div>

        {/* Transcript */}
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Transcript</label>
          {recording.status === 'TRANSCRIBING' ? (
            <p className="text-gray-400 italic">Transcription in progress...</p>
          ) : editing ? (
            <textarea value={transcript} onChange={e => setTranscript(e.target.value)}
              rows={12} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          ) : (
            <div className="bg-gray-50 rounded p-4 text-sm text-gray-800 whitespace-pre-wrap min-h-[100px]">
              {recording.transcript || <span className="text-gray-400 italic">No transcript available</span>}
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="mt-4 pt-4 border-t text-sm text-gray-500">
          <p>File: {recording.fileName}</p>
          {recording.createdAt && <p>Created: {new Date(recording.createdAt).toLocaleString()}</p>}
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Recording</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this recording? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
